import { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import { useTheme } from '../../context/ThemeContext'
import { formatCurrency } from '../../utils/formatters'

const MOVEMENT_COLORS = {
  'Starting ARR': '#6b7280',
  'New Business': '#22c55e',
  'Expansion Upsell': '#4ade80',
  'Expansion Resell': '#86efac',
  'Contraction Downsell': '#f59e0b',
  'Contraction Resell': '#fbbf24',
  'Churn': '#ef4444',
  'Ending ARR': '#3b82f6'
}

export default function WaterfallChart({ data }) {
  const containerRef = useRef(null)
  const { isDark } = useTheme()

  useEffect(() => {
    if (!containerRef.current || !data || data.length === 0) return

    // Clear previous chart
    d3.select(containerRef.current).selectAll('*').remove()

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight
    const margin = { top: 20, right: 20, bottom: 60, left: 70 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    // Process data to calculate running totals
    let runningTotal = 0
    const processedData = data.map((d, i) => {
      const isFirst = i === 0
      const isLast = i === data.length - 1

      let start, end
      if (isFirst || isLast) {
        start = 0
        end = d.value
        runningTotal = d.value
      } else {
        start = runningTotal
        end = runningTotal + d.value
        runningTotal = end
      }

      return {
        ...d,
        start: Math.min(start, end),
        end: Math.max(start, end),
        isPositive: d.value >= 0,
        isTotal: isFirst || isLast
      }
    })

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Scales
    const xScale = d3.scaleBand()
      .domain(processedData.map(d => d.label))
      .range([0, innerWidth])
      .padding(0.3)

    const maxValue = d3.max(processedData, d => d.end) * 1.1
    const yScale = d3.scaleLinear()
      .domain([0, maxValue])
      .range([innerHeight, 0])

    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(yScale.ticks(5))
      .join('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .attr('stroke', isDark ? '#374151' : '#e5e7eb')
      .attr('stroke-dasharray', '2,2')

    // Bars
    g.selectAll('.bar')
      .data(processedData)
      .join('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.label))
      .attr('y', d => yScale(d.end))
      .attr('width', xScale.bandwidth())
      .attr('height', d => Math.abs(yScale(d.start) - yScale(d.end)))
      .attr('fill', d => MOVEMENT_COLORS[d.label] || '#6b7280')
      .attr('rx', 4)

    // Connector lines
    g.selectAll('.connector')
      .data(processedData.slice(0, -1))
      .join('line')
      .attr('class', 'connector')
      .attr('x1', d => xScale(d.label) + xScale.bandwidth())
      .attr('x2', (d, i) => xScale(processedData[i + 1].label))
      .attr('y1', d => yScale(d.isTotal ? d.end : d.end))
      .attr('y2', d => yScale(d.isTotal ? d.end : d.end))
      .attr('stroke', isDark ? '#6b7280' : '#9ca3af')
      .attr('stroke-dasharray', '3,3')
      .attr('stroke-width', 1)

    // Value labels
    g.selectAll('.value-label')
      .data(processedData)
      .join('text')
      .attr('class', 'value-label')
      .attr('x', d => xScale(d.label) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.end) - 5)
      .attr('text-anchor', 'middle')
      .attr('fill', isDark ? '#d1d5db' : '#4b5563')
      .attr('font-size', '11px')
      .attr('font-weight', '500')
      .text(d => formatCurrency(d.isTotal ? d.end : d.value, { compact: true }))

    // X Axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .attr('fill', isDark ? '#9ca3af' : '#6b7280')
      .attr('font-size', '10px')
      .attr('transform', 'rotate(-35)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em')

    g.select('.domain').remove()
    g.selectAll('.tick line').remove()

    // Y Axis
    g.append('g')
      .call(d3.axisLeft(yScale)
        .ticks(5)
        .tickFormat(d => formatCurrency(d, { compact: true })))
      .selectAll('text')
      .attr('fill', isDark ? '#9ca3af' : '#6b7280')
      .attr('font-size', '11px')

    g.select('.domain').remove()
    g.selectAll('.tick line').remove()

  }, [data, isDark])

  return (
    <div ref={containerRef} className="w-full h-full" />
  )
}
